/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {useQueryClient} from 'react-query'
import {ID, secondsToDays, secondsToHours, secondsToHoursSimplified} from '../../../helpers'
import {UsersSymbolGroupCard} from '../../../../app/modules/project/components/projects-list/card/UsersSymbolGroupCard'
import {useQueryResponse} from '../../../../app/modules/project/components/core/QueryResponseProvider'
import {ProjectListActions} from '../../../../app/modules/project/components/projects-list/components/actions/ProjectListActions'
import {useAuth} from '../../../../app/modules/auth'
import {isFunction} from 'formik'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import { Team } from '../../modals/team/core/_models'
interface IconUserModel {
  name: string
  avatar?: string
  color?: string
  initials?: string
}

type Props = {
  id?: ID
  name?: string
  subtitle?: string
  client?: string
  budgetedHours?: number
  users: Array<IconUserModel>
  begin_date: string
  end_date: string
  closed_at?: string | null
  totalTime?: number | null
  profit_margin?: number | null
  is_private: boolean
  teams?: Team[]
  criticality_score?: number
  critical_tasks_count?: number
  late_tasks_count?: number
  high_priority_tasks_count?: number
  overdue_1_3_tasks_count?: number
  overdue_4_7_tasks_count?: number
  overdue_8_plus_tasks_count?: number
  due_soon_high_critical_tasks_count?: number
  total_tasks?: number
  type?: number
}

const ProjectCard: FC<Props> = ({
  id,
  name,
  budgetedHours,
  client,
  users,
  begin_date,
  end_date,
  closed_at,
  totalTime,
  profit_margin,
  is_private,
  teams,
  criticality_score,
  overdue_1_3_tasks_count,
  overdue_4_7_tasks_count,
  overdue_8_plus_tasks_count,
  due_soon_high_critical_tasks_count,
  total_tasks,
  type,
}) => {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const {query} = useQueryResponse()
  const [progressedTime, setProgressedTime] = useState<number>(0)
  const [progressedTimePercent, setProgressedTimePercent] = useState<number>(0)
  const [progressBarColor, setprogressBarColor] = useState<string>('bg-primary')
  const now = new Date()
  const begin = begin_date ? new Date(begin_date) : null
  const end = end_date ? new Date(end_date) : null
  const closedAt = closed_at ? new Date(closed_at) : null
  var status = intl.formatMessage({id: 'PROJECT.LABEL.IN_PROGRESS'})
  var statusColor = 'light-primary'
  const deadline = end && begin ? secondsToDays(Math.abs(end.getTime() - begin.getTime())) : null
  const {currentUser} = useAuth()

  useEffect(() => {
    calculateProgress()
  }, [closed_at])

  if (begin && now < begin) {
    status = intl.formatMessage({id: 'PROJECT.LABEL.NOT.START'})
    statusColor = 'light-warning'
  }

  if (end && now > end) {
    status = intl.formatMessage({id: 'PROJECT.LABEL.DELAYED'})
    statusColor = 'light-danger'
  }

  if (closedAt) {
    status = intl.formatMessage({id: 'PROJECT.LABEL.CLOSED'})
    statusColor = 'light-success'
  }

  if (closedAt && end && closedAt > end) {
    status = intl.formatMessage({id: 'PROJECT.LABEL.CLOSED_WITH_DELAY'})
    statusColor = 'light-danger'
  }

  // Definir badge de criticidade
  const getCriticalityBadge = () => {
    if (!criticality_score || criticality_score === 0) return null

    if (criticality_score >= 75) {
      return {label: 'CRÍTICO', className: 'project-criticality-badge project-criticality-critical'}
    } else if (criticality_score >= 50) {
      return {label: 'URGENTE', className: 'project-criticality-badge project-criticality-urgent'}
    } else if (criticality_score >= 25) {
      return {label: 'ATENÇÃO', className: 'project-criticality-badge project-criticality-attention'}
    }
    return null
  }

  const criticalityBadge = getCriticalityBadge()
  const projectDeadlinePoints = end && !closedAt
    ? now > end
      ? 30
      : (end.getTime() - now.getTime()) <= (3 * 24 * 60 * 60 * 1000) && (end.getTime() - now.getTime()) >= 0
        ? 12
        : 0
    : 0
  const overdue1to3Count = overdue_1_3_tasks_count ?? 0
  const overdue4to7Count = overdue_4_7_tasks_count ?? 0
  const overdue8PlusCount = overdue_8_plus_tasks_count ?? 0
  const overdueTasksTotalCount = overdue1to3Count + overdue4to7Count + overdue8PlusCount
  const overdueTasksPointsRaw = overdue1to3Count * 4 + overdue4to7Count * 8 + overdue8PlusCount * 12
  const overdueTasksPoints = Math.min(overdueTasksPointsRaw, 45)
  const dueSoonHighCriticalCount = due_soon_high_critical_tasks_count ?? 0
  const dueSoonRiskPointsRaw = dueSoonHighCriticalCount * 6
  const dueSoonRiskPoints = Math.min(dueSoonRiskPointsRaw, 25)

  // Selecionar os 3 indicadores mais relevantes para exibir
  const getTopMetrics = () => {
    const metrics = []
    
    // Sempre incluir horas lançadas (se houver)
    if (totalTime && totalTime > 0) {
      metrics.push({
        key: 'hours',
        label: intl.formatMessage({id: 'PROJECT.LABEL.RECORDED_HOURS'}),
        value: secondsToHoursSimplified(totalTime),
        priority: 1
      })
    }
    
    // Tarefas abertas (alta prioridade)
    if (total_tasks && total_tasks > 0) {
      metrics.push({
        key: 'tasks',
        label: 'Tarefas:',
        value: `${total_tasks}`,
        priority: 2
      })
    }
    
    // Margem de lucro (administrador)
    if (currentUser?.administrator && profit_margin !== null && profit_margin !== undefined) {
      metrics.push({
        key: 'margin',
        label: intl.formatMessage({id: 'PROJECT.LABEL.PROFIT_MARGIN'}),
        value: profit_margin + '%',
        priority: 3
      })
    }
    
    // Horas orçadas (administrador)
    if (currentUser?.administrator && budgetedHours && budgetedHours > 0) {
      metrics.push({
        key: 'budgeted',
        label: intl.formatMessage({id: 'PROJECT.LABEL.BUDGETED_HOURS'}),
        value: secondsToHoursSimplified(budgetedHours),
        priority: 4
      })
    }
    
    // Retornar os 3 primeiros (ou menos se não houver 3)
    return metrics.slice(0, 3)
  }

  const topMetrics = getTopMetrics()

  const calculateProgress = () => {
    if (begin && end && deadline) {
      if (now < begin) {
        setProgressedTimePercent(0)
      }
      if (now > end) {
        setprogressBarColor('bg-danger')
        setProgressedTimePercent(100)

        if (!closed_at) {
          var progressed = secondsToDays(Math.abs(now.getTime() - begin.getTime()))
          setProgressedTime(progressed)
        } else {
          const closedAtInDate = new Date(closed_at)
          var progressed = secondsToDays(Math.abs(closedAtInDate.getTime() - begin.getTime()))
          setProgressedTime(progressed)
        }
      }
      if (now >= begin && now < end) {
        if (!closed_at) {
          var progressed = secondsToDays(Math.abs(now.getTime() - begin.getTime()))
          var progressPercent = (progressed * 100) / deadline
          setProgressedTime(progressed)
          setProgressedTimePercent(progressPercent)
          setprogressBarColor('bg-primary')
        } else {
          const closedAtInDate = new Date(closed_at)
          var progressed = secondsToDays(Math.abs(closedAtInDate.getTime() - begin.getTime()))
          var progressPercent = (progressed * 100) / deadline
          setProgressedTime(progressed)
          setProgressedTimePercent(progressPercent)
          setprogressBarColor('bg-success')
        }
      }
    }
  }

  return (
    <div className='card h-100 border border-2 border-gray-200 border-hover'>
      <div className='card-header flex-grow-1 align-items-start border-0 p-5 pb-0'>
        <div className='d-flex align-items-start justify-content-between w-100 gap-2'>
          {/* Título com toda a largura disponível (só disputa espaço com o
              menu de ações, não mais com o badge de status — antes os dois
              ficavam em colunas fixas de 8/4, e o título quebrava mais do
              que precisava). */}
          <div className='d-flex align-items-start min-w-0'>
            {is_private && (
              <OverlayTrigger
                placement='top'
                delay={{show: 50, hide: 100}}
                overlay={
                  <Tooltip>
                    Este projeto é privado. Apenas as equipes vinculadas têm acesso para
                    visualizar e editar este conteúdo.
                  </Tooltip>
                }
              >
                <i className='bi bi-lock-fill me-2 fs-7 mt-2 flex-shrink-0'></i>
              </OverlayTrigger>
            )}
            <Link to={`/projects/${id}`} className='fs-3 fw-bolder text-dark'>
              {name}
            </Link>
          </div>
          {(currentUser?.administrator || currentUser?.manager) && (
            <div className='flex-shrink-0'>
              <ProjectListActions id={id} closed_at={closed_at} teams={teams} projectType={type} />
            </div>
          )}
        </div>

        {/* Linha de metadados: cliente, status e criticidade — sempre numa
            linha própria abaixo do título (ordem confirmada pelo usuário;
            status antes do cliente deixava "Atenção" longe da criticidade
            e o cliente preso entre as duas badges). */}
        <div className='d-flex flex-wrap align-items-center gap-2 mt-2'>
          {client && <span className='fs-5 text-gray-700'>{client}</span>}
          <span className={`badge badge-${statusColor} fw-bolder`}>{status}</span>
          {criticalityBadge && (
            <OverlayTrigger
              placement='top'
              delay={{show: 100, hide: 100}}
              overlay={
                <Tooltip>
                  <div className='text-start'>
                    <strong>🎯 Score de Criticidade: {criticality_score} pontos</strong>
                    <div className='mt-2 mb-1' style={{borderBottom: '1px solid rgba(255,255,255,0.2)'}}></div>
                    <div className='mt-2'>
                      <strong>Detalhamento:</strong>
                      <div className='mt-1'>
                        🔴 {projectDeadlinePoints === 30 ? 'Projeto em atraso' : projectDeadlinePoints === 12 ? 'Projeto vence em até 3 dias' : 'Projeto dentro do prazo'}: <strong>+{projectDeadlinePoints} pts</strong>
                      </div>
                      <div className='mt-1'>
                        ⏰ {overdueTasksTotalCount} {overdueTasksTotalCount === 1 ? 'tarefa atrasada' : 'tarefas atrasadas'} ({overdue1to3Count} 1-3d, {overdue4to7Count} 4-7d, {overdue8PlusCount} 8+d): <strong>+{overdueTasksPoints} pts</strong>{overdueTasksPointsRaw > 45 ? ' (cap 45)' : ''}
                      </div>
                      <div className='mt-1'>
                        🎯 {dueSoonHighCriticalCount} {dueSoonHighCriticalCount === 1 ? 'tarefa alta/crítica com prazo até 3 dias' : 'tarefas altas/críticas com prazo até 3 dias'}: <strong>+{dueSoonRiskPoints} pts</strong>{dueSoonRiskPointsRaw > 25 ? ' (cap 25)' : ''}
                      </div>
                    </div>
                    <div className='mt-2' style={{fontSize: '0.85em', opacity: 0.8}}>
                      Clique para ver detalhes do projeto
                    </div>
                  </div>
                </Tooltip>
              }
            >
              <span className={`${criticalityBadge.className} cursor-pointer`}>
                {criticalityBadge.label}
              </span>
            </OverlayTrigger>
          )}
        </div>
      </div>
      <div className='card-body flex-grow-0 p-5 p-md-5'>
        {/* <p className='text-gray-400 fw-bold fs-5 mt-1 mb-7'>{subtitle}</p> */}
        {/* Indicadores principais - sempre 3 (ou menos se não houver dados) */}
        {topMetrics.length > 0 && (
          <div className='d-flex mb-5 gap-2 flex-wrap'>
            {topMetrics.map((metric) => (
              <div 
                key={metric.key} 
                className='border border-gray-200 border-dashed rounded flex-fill py-2 px-3' 
                style={{minWidth: '85px'}}
              >
                <div className='fs-8 text-gray-600 fw-bold'>
                  {metric.label}
                </div>
                <div className='fw-bold text-gray-800 fs-6'>
                  {metric.value}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Barra de Progresso Melhorada */}
        {deadline ? (
          <div className='mb-5'>
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <span className='fw-bold text-gray-700 fs-7'>
                Progresso: {progressedTimePercent.toFixed(0)}%
              </span>
              <span className='fw-bold fs-7 text-gray-600'>
                {progressedTime}/{deadline} dias
              </span>
            </div>
            <div className='progress h-8px bg-light'>
              <div
                className={`progress-bar ${progressBarColor === 'bg-danger' ? 'bg-danger' : progressBarColor === 'bg-success' ? 'bg-success' : 'bg-brand-progress'} progress-bar-striped`}
                role='progressbar'
                style={{width: `${Math.min(progressedTimePercent, 100)}%`}}
                aria-valuenow={progressedTimePercent}
                aria-valuemin={0}
                aria-valuemax={100}
              ></div>
            </div>
          </div>
        ) : (
          <div className='mb-5'>
            <div className='text-center py-2 bg-light rounded'>
              <span className='text-muted fs-7 fw-bold'>
                <i className='bi bi-calendar-x me-1'></i>
                Sem prazo definido
              </span>
            </div>
          </div>
        )}
        
        <div className='symbol symbol-35px symbol-circle'>
          <UsersSymbolGroupCard users={users} classes='justify-content-start' />
        </div>
      </div>
    </div>
  )
}

export {ProjectCard}
